import { Product, ProductAssessment } from './product-roadmap-constants';

// Scores run 1-10 in five two-point anchor bands (product-brain ROADMAP.md, "Effort × Impact
// assessments"). The quadrant lines sit at 5.5 -- between two bands -- so no whole-number score
// ever lands on a line and every product belongs to exactly one quadrant.
export const SCORE_MIN = 1;
export const SCORE_MAX = 10;
export const QUADRANT_LINE = 5.5;

export const Quadrant = {
  QUICK_WIN: 'quick-win',
  BIG_BET: 'big-bet',
  FILL_IN: 'fill-in',
  CAUTION: 'caution'
} as const;
export type QuadrantType = (typeof Quadrant)[keyof typeof Quadrant];

export interface QuadrantDef {
  key: QuadrantType;
  label: string;
  description: string;
}

// Display order: the two high-impact quadrants first. "Caution" was "Reconsider" in the first
// drafts -- renamed by Sean (2026-10-17) because this page is visible company-wide and an owner
// could read "Reconsider" as a verdict on their product.
export const QUADRANTS: QuadrantDef[] = [
  { key: Quadrant.QUICK_WIN, label: 'Quick wins', description: 'High impact, low effort' },
  { key: Quadrant.BIG_BET, label: 'Big bets', description: 'High impact, high effort' },
  { key: Quadrant.FILL_IN, label: 'Fill-ins', description: 'Low impact, low effort' },
  { key: Quadrant.CAUTION, label: 'Caution', description: 'Low impact, high effort' }
];

export const quadrantOf = ({ impact, effort }: Pick<ProductAssessment, 'impact' | 'effort'>) => {
  const highImpact = impact > QUADRANT_LINE;
  const lowEffort = effort <= QUADRANT_LINE;
  if (highImpact) return lowEffort ? Quadrant.QUICK_WIN : Quadrant.BIG_BET;
  return lowEffort ? Quadrant.FILL_IN : Quadrant.CAUTION;
};

// The time each effort band stands for, shown under the axis so a reader does not need the
// anchor table to read the chart. Keyed by the band's midpoint on the 1-10 scale.
export const EFFORT_BAND_HINTS: { at: number; label: string }[] = [
  { at: 1.5, label: '≤2 wks' },
  { at: 3.5, label: '2–6 wks' },
  { at: 5.5, label: '1–3 mo' },
  { at: 7.5, label: '3–6 mo' },
  { at: 9.5, label: '6+ mo' }
];

export type ScoredProduct = Product & { assessment: ProductAssessment };

// What the 2x2 plots: assessable products that carry both scores. Deployed and parked products
// are never assessable; an assessable product with no assessment is listed as "not yet scored".
export const scoredProducts = (products: Product[]): ScoredProduct[] =>
  products.filter((p): p is ScoredProduct => p.assessable === true && Boolean(p.assessment));

export const unscoredProducts = (products: Product[]): Product[] =>
  products.filter(p => p.assessable === true && !p.assessment);

// A product's dot takes the color of its first-listed function -- PRODUCT-FACETS.md lists the
// primary function first. The card and tooltip show every function.
export const primaryFunction = (product: Product): string => product.function[0] ?? '';

export interface PackedPoint {
  product: ScoredProduct;
  // Offset from the shared (effort, impact) point, in multiples of the dot spacing.
  dx: number;
  dy: number;
}

// Products with identical scores would draw on top of one another. Each group sharing a point is
// laid out in a small grid centred on it: ceil(sqrt(n)) columns, the last row centred, sorted by
// name so a product keeps the same spot from render to render (no random jitter). The chart
// multiplies dx/dy by its dot spacing.
export const packPoints = (products: ScoredProduct[]): PackedPoint[] => {
  const groups = new Map<string, ScoredProduct[]>();
  products.forEach(p => {
    const key = `${p.assessment.effort},${p.assessment.impact}`;
    groups.set(key, [...(groups.get(key) ?? []), p]);
  });
  const packed: PackedPoint[] = [];
  groups.forEach(group => {
    const sorted = [...group].sort((a, b) => a.name.localeCompare(b.name));
    const n = sorted.length;
    const cols = Math.ceil(Math.sqrt(n));
    const rows = Math.ceil(n / cols);
    sorted.forEach((product, k) => {
      const row = Math.floor(k / cols);
      const col = k % cols;
      const inRow = row === rows - 1 ? n - row * cols : cols;
      packed.push({ product, dx: col - (inRow - 1) / 2, dy: row - (rows - 1) / 2 });
    });
  });
  return packed;
};

// Quadrant lists under the chart: highest impact first, then least effort, then name.
export const sortForQuadrantList = (a: ScoredProduct, b: ScoredProduct): number =>
  b.assessment.impact - a.assessment.impact ||
  a.assessment.effort - b.assessment.effort ||
  a.name.localeCompare(b.name);
